import axios from "axios";
import Cookies from "js-cookie";
import store from "@/store/index";
import router from "@/router/index";

import { EventBus } from "@/main.js";
import {
  ACCESS_COOKIE,
  REFRESH_COOKIE,
  createRefresher,
} from "@/config/service/refreshToken";

const standard = axios.create({
  baseURL: process.env.VUE_APP_API_HOST,
  timeout: 100000,
  transformResponse: [
    function (data) {
      return data;
    },
  ],
});

// Estado desta aba. Http.getHttp() monta o header a partir do store, então uma
// aba que só adota o token do cookie sem atualizar o store continuaria mandando
// o antigo em toda request.
function syncToken(access_token) {
  store.commit("authSuccess", access_token);
  standard.defaults.headers.common["Authorization"] = access_token;
}

export function persistTokens(data) {
  const access_token = "Bearer " + data.access_token;

  Cookies.set(ACCESS_COOKIE, access_token, { expires: 365 });

  if (data.refresh_token) {
    Cookies.set(REFRESH_COOKIE, data.refresh_token, {
      expires: new Date(Date.now() + 8 * 60 * 60 * 1000),
    });
  }

  syncToken(access_token);

  return access_token;
}

function requestToken(refresh_token) {
  // axios cru de propósito: passar pelo `standard` faria o 401 desta própria
  // chamada cair neste interceptor e recursar. O timeout existe porque a
  // renovação segura a trava entre abas enquanto está no ar.
  return axios
    .post(
      process.env.VUE_APP_API_HOST + "/oauth/token",
      {
        grant_type: "refresh_token",
        refresh_token: refresh_token,
        client_id: process.env.VUE_APP_OAUTH_CLIENT_ID,
        client_secret: process.env.VUE_APP_OAUTH_CLIENT_SECRET,
        scope: "*",
      },
      { timeout: 30000 }
    )
    .then(function (resp) {
      return typeof resp.data === "string" ? JSON.parse(resp.data) : resp.data;
    });
}

const refreshAccessToken = createRefresher({
  requestToken,
  persistTokens,
  adoptToken: syncToken,
});

// Duas tentativas: a primeira pode adotar o token que outra aba renovou; se
// esse também já venceu (aba ociosa por mais de 30min), a segunda faz a
// renovação de verdade com o refresh atual.
const MAX_REFRESH_ATTEMPTS = 2;

standard.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (err) {
    const config = err.config || {};


    // Cancelamentos via CancelToken são intencionais — não exibir toast (VSUS-991)
    if (axios.isCancel(err)) return Promise.reject(err);

    // O access token agora dura 30min. Em 401, tenta renovar pelo refresh_token
    // e repetir a request — é isso que faz a sessão expirar por inatividade em
    // vez de cair no meio do uso. Sessões de impersonation não têm
    // refresh_token, então caem direto no tratamento de 401 abaixo.
    const attempts = config.__refreshAttempts || 0;

    if (
      err.response &&
      err.response.status === 401 &&
      attempts < MAX_REFRESH_ATTEMPTS &&
      Cookies.get(REFRESH_COOKIE)
    ) {
      config.__refreshAttempts = attempts + 1;

      // Passa o token que tomou 401: se outra request ou aba já renovou, o
      // refresher devolve o token novo em vez de gastar outro refresh.
      const usedToken = config.headers && config.headers.Authorization;

      let renewed;
      try {
        renewed = await refreshAccessToken(usedToken);
      } catch (refreshError) {
        // Falha passageira na renovação (rede, timeout, 5xx): a sessão e o
        // refresh continuam; só esta request falha, sem deslogar.
        if (!config.suppressErrorNotify) {
          EventBus.$emit("errorNotify", "Erro ao executar a requisição");
        }
        return Promise.reject(err);
      }

      // Repetir com o mesmo token que acabou de tomar 401 não adianta.
      if (renewed && renewed !== usedToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = renewed;

        return standard(config);
      }
    }


    if (!config.suppressErrorNotify) {
      if (err.response) {
        switch (err.response.status) {
          case 422: {
            if (err.response.request.responseType == "blob") {
              EventBus.$emit(
                "errorNotify",
                "Erro ao solicitar o download deste arquivo",
              );
            } else {
              let data = err.response.data;
              if (typeof data == "string") {
                data = JSON.parse(data);
              }

              let errorShown = false;

              if (data.errors && typeof data.errors === "object") {
                for (let campo in data.errors) {
                  if (
                    Array.isArray(data.errors[campo]) &&
                    data.errors[campo].length > 0
                  ) {
                    EventBus.$emit("errorNotify", {
                      message: data.errors[campo][0],
                      title: data.title || null,
                      originalData: data,
                    });
                    errorShown = true;
                    break;
                  }
                }
              }

              if (!errorShown) {
                for (let campo in data) {
                  if (
                    campo !== "message" &&
                    campo !== "title" &&
                    Array.isArray(data[campo])
                  ) {
                    if (data[campo].length > 0) {
                      EventBus.$emit("errorNotify", {
                        message: data[campo][0],
                        title: data.title || null,
                        originalData: data,
                      });
                      errorShown = true;
                      break;
                    }
                  }
                }
              }

              if (!errorShown && data.message) {
                EventBus.$emit("errorNotify", {
                  message: data.message,
                  title: data.title || null,
                  originalData: data,
                });
                errorShown = true;
              }

              if (!errorShown) {
                EventBus.$emit("errorNotify", "Erro de validação");
              }
            }
            break;
          }
          case 400: {
            EventBus.$emit(
              "errorNotify",
              "400 - Algum parâmetro obrigatório não foi enviado ou é inválido.",
            );
            break;
          }
          case 401: {
            // Chegou aqui é porque não havia refresh ou ele já não valia mais.
            //
            // Zerar header do axios e Vuex de forma SÍNCRONA, ANTES de navegar.
            // Sem isso, o router.push('/') renderizava o novo componente com o
            // token expirado ainda no header e em auth.state.access_token —
            // qualquer request disparada ali voltava 401 e entrava em loop até
            // o window.location.href do logoutRequest disparar. (INC0786722)
            delete standard.defaults.headers.common["Authorization"];
            store.commit("authSuccess", "");
            Cookies.remove(REFRESH_COOKIE);

            // logoutRequest: cleanup (cookies restantes, permissões, Crisp, hard reload).
            // isExpiredSession: sinaliza que é uma sessão expirada — o cleanup
            // autenticado do ERP (remove-subusers-erp) deve ser pulado para
            // evitar outro ciclo de 401. (INC0786722-fix2)
            store.dispatch("logoutRequest", { isExpiredSession: true }).catch(function (err) {});
            router.push("/");
            EventBus.$emit(
              "errorNotify",
              "401 - Usuário não autorizado. Refaça o login e tente novamente",
            );
            break;
          }
          case 404: {
            EventBus.$emit(
              "errorNotify",
              "404 - A URL configurada na requisição não existe.",
            );
            break;
          }
          case 405: {
            EventBus.$emit("errorNotify", "405 - Método não permitido.");
            break;
          }
          case 408: {
            EventBus.$emit(
              "errorNotify",
              "408 - Aplicação não retornou uma resposta dentro do tempo esperado.",
            );
            break;
          }
          case 500: {
            EventBus.$emit(
              "errorNotify",
              "500 - A requisição foi enviada, porém ele retornou algum erro interno",
            );
            break;
          }
          default: {
            EventBus.$emit("errorNotify", "Erro ao executar a requisição");
            break;
          }
        }
      } else {
        EventBus.$emit("errorNotify", "Erro ao executar a requisição");
      }
    }

    return Promise.reject(err);
  },
);

if (Cookies.get("access_token")) {
  standard.defaults.headers.common["Authorization"] =
    Cookies.get("access_token");
}
standard.defaults.headers.common["Access-Control-Allow-Origin"] = "*";

export default standard;
